import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { IoEyeOff, IoEye } from "react-icons/io5";
import { IoMdArrowRoundForward } from "react-icons/io";

import { useAppContext } from "../contexts/AppContextProvider";
import { inputUsernameSanitation } from "../utils/inputValidations";

/* ─── Inline field sub-component (no MUI) ─────────────────────────────────── */
const LoginField = ({
  id,
  label,
  type = "text",
  placeholder,
  value,
  onChange,
  required,
  children, // slot for the eye-toggle button
}) => (
  <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    <label
      htmlFor={id}
      style={{
        fontSize: "12px",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: "var(--text-secondary)",
      }}
    >
      {label}
    </label>
    <div style={{ position: "relative" }}>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete="off"
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: children ? "10px 42px 10px 14px" : "10px 14px",
          borderRadius: "10px",
          border: "1px solid var(--border-color)",
          background: "var(--background-secondary, var(--background-main))",
          color: "var(--text-primary)",
          fontSize: "14px",
          outline: "none",
          transition: "border-color 0.2s, box-shadow 0.2s",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "var(--accent-color, #059669)";
          e.target.style.boxShadow = "0 0 0 3px rgba(5,150,105,0.12)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "var(--border-color)";
          e.target.style.boxShadow = "none";
        }}
      />
      {children}
    </div>
  </div>
);

/* ─── Main Login component ─────────────────────────────────────────────────── */
const Login = () => {
  const { isLoggedIn, apiCallLogin, isLoading, partnerInfo } = useAppContext();

  const [corporateId, setCorporateId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const theme = localStorage.getItem("theme") || "dark";

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!corporateId.trim() || !agentId.trim() || !password.trim()) {
      return; // HTML `required` handles the empty-field feedback
    }

    apiCallLogin(corporateId, agentId, password);
  };

  useEffect(() => {
    localStorage.setItem("theme", theme);

    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      style={{ background: "var(--background-main)" }}
    >
      {/* ── Keyframe animations ── */}
      <style>{`
        @keyframes orb-drift-1 {
          0%   { transform: translate(0, 0) scale(1); }
          33%  { transform: translate(40px, -30px) scale(1.05); }
          66%  { transform: translate(-20px, 50px) scale(0.97); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes orb-drift-2 {
          0%   { transform: translate(0, 0) scale(1); }
          33%  { transform: translate(-50px, 30px) scale(1.04); }
          66%  { transform: translate(30px, -40px) scale(0.98); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes orb-drift-3 {
          0%   { transform: translate(0, 0) scale(1); }
          50%  { transform: translate(25px, -20px) scale(1.06); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .login-orb-1 { animation: orb-drift-1 18s ease-in-out infinite; }
        .login-orb-2 { animation: orb-drift-2 22s ease-in-out infinite; }
        .login-orb-3 { animation: orb-drift-3 14s ease-in-out infinite; }
        .login-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          display: inline-block;
        }
      `}</style>

      {/* Ambient orb 1 — top-left, green */}
      <div
        className="login-orb-1 pointer-events-none absolute"
        style={{
          top: "-10%",
          left: "-8%",
          width: "520px",
          height: "520px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(5,150,105,0.38) 0%, transparent 70%)",
          filter: "blur(24px)",
        }}
      />

      {/* Ambient orb 2 — bottom-right, indigo */}
      <div
        className="login-orb-2 pointer-events-none absolute"
        style={{
          bottom: "-12%",
          right: "-10%",
          width: "600px",
          height: "600px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(99,102,241,0.28) 0%, transparent 70%)",
          filter: "blur(30px)",
        }}
      />

      {/* Ambient orb 3 — center accent */}
      <div
        className="login-orb-3 pointer-events-none absolute"
        style={{
          top: "40%",
          left: "55%",
          width: "360px",
          height: "360px",
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(5,150,105,0.20) 0%, transparent 70%)",
          filter: "blur(20px)",
        }}
      />

      {/* Dot-grid SVG */}
      <svg
        className="pointer-events-none absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        style={{ opacity: 0.55 }}
      >
        <defs>
          <pattern
            id="login-dots"
            x="0"
            y="0"
            width="28"
            height="28"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="1.5" cy="1.5" r="1.5" fill="#94a3b8" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#login-dots)" />
      </svg>

      {/* Diagonal line grid */}
      <svg
        className="pointer-events-none absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        style={{ opacity: 0.14 }}
      >
        <defs>
          <pattern
            id="login-diag"
            x="0"
            y="0"
            width="48"
            height="48"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="48"
              stroke="var(--accent-color)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#login-diag)" />
      </svg>

      {/* ── Login card ── */}
      <div
        className="w-full max-w-md rounded-3xl border shadow-2xl p-8 backdrop-blur-md relative z-10"
        style={{
          background: "var(--background-primary)",
          borderColor: "var(--border-color)",
          boxShadow:
            "0 0 0 1px var(--border-color), 0 8px 16px rgba(0,0,0,0.08), 0 24px 64px rgba(0,0,0,0.12), 0 0 48px rgba(5,150,105,0.14)",
        }}
      >
        {/* Card heading */}
        <h1 className="text-3xl font-bold text-center text-themeColor3">
          WaBM Partner
        </h1>

        {isLoggedIn ? (
          /* ── Already-logged-in state ── */
          <div>
            <h2
              className="mt-6 text-xl font-semibold text-center"
              style={{ color: "var(--text-primary)" }}
            >
              Good to see you back 👋
            </h2>

            {/* Partner info pill */}
            <div
              className="mt-4 rounded-xl p-4 flex flex-col gap-2"
              style={{
                background:
                  "var(--background-secondary, var(--background-main))",
                border: "1px solid var(--border-color)",
              }}
            >
              {partnerInfo?.corporateId && (
                <div className="flex items-center gap-2">
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "var(--text-secondary)",
                    }}
                  >
                    Corp ID:
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: "6px",
                      background: "rgba(5,150,105,0.15)",
                      color: "#059669",
                    }}
                  >
                    {partnerInfo.corporateId}
                  </span>
                </div>
              )}
              <p
                className="font-semibold"
                style={{ color: "var(--text-primary)" }}
              >
                {partnerInfo?.agentName || "Partner"}
              </p>
              <p style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                Ready to manage your partner operations?
              </p>
            </div>

            <NavLink to="/dashboard">
              <button
                type="button"
                className="mt-6 w-full flex items-center justify-center gap-2 font-semibold rounded-xl"
                style={{
                  padding: "12px",
                  background:
                    "linear-gradient(135deg, #059669 0%, #4f46e5 100%)",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "15px",
                  transition: "opacity 0.2s, transform 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.9";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                Go to Dashboard <IoMdArrowRoundForward />
              </button>
            </NavLink>
          </div>
        ) : (
          /* ── Login form ── */
          <>
            <p
              className="text-center mt-2 mb-8"
              style={{ color: "var(--text-secondary)", fontSize: "14px" }}
            >
              Login to access your partner dashboard.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Corp ID */}
              <LoginField
                id="partner-corp-id"
                label="Corp ID"
                placeholder="Enter your Corporate ID"
                value={corporateId}
                onChange={(e) =>
                  setCorporateId(inputUsernameSanitation(e.target.value))
                }
                required
              />

              {/* Agent ID */}
              <LoginField
                id="partner-agent-id"
                label="Agent ID"
                placeholder="Enter your Agent ID"
                value={agentId}
                onChange={(e) =>
                  setAgentId(inputUsernameSanitation(e.target.value))
                }
                required
              />

              {/* Password */}
              <LoginField
                id="partner-password"
                label="Password"
                type={isPasswordVisible ? "text" : "password"}
                placeholder="Enter your Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              >
                {/* Eye toggle */}
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setIsPasswordVisible((prev) => !prev)}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--text-secondary)",
                    display: "flex",
                    alignItems: "center",
                    padding: "4px",
                    borderRadius: "4px",
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.color = "#059669")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.color = "var(--text-secondary)")
                  }
                  aria-label={
                    isPasswordVisible ? "Hide password" : "Show password"
                  }
                >
                  {isPasswordVisible ? (
                    <IoEye size={18} />
                  ) : (
                    <IoEyeOff size={18} />
                  )}
                </button>
              </LoginField>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="mt-2 w-full flex items-center justify-center gap-2 font-semibold rounded-xl"
                style={{
                  padding: "13px",
                  background: isLoading
                    ? "rgba(5,150,105,0.5)"
                    : "linear-gradient(135deg, #059669 0%, #4f46e5 100%)",
                  color: "#fff",
                  border: "none",
                  cursor: isLoading ? "not-allowed" : "pointer",
                  fontSize: "15px",
                  transition: "opacity 0.2s, transform 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.opacity = "0.9";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {isLoading ? (
                  <>
                    <span className="login-spinner" />
                    Signing in…
                  </>
                ) : (
                  "Login to Dashboard"
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
